// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import {
  DeclarativeItemBase,
  getAllowedDeclarativeComponentMapping,
} from "./DeclarativeContentTypes";

import React from "react";
import useCenteredStyle from "style/useCenteredStyle";

interface DeclarativeItemRendererProps {
  item: DeclarativeItemBase;
}

function isDeclarativeItem(obj: any): obj is DeclarativeItemBase {
  return (
    typeof obj === "object" &&
    "type" in obj &&
    "props" in obj &&
    (obj.type in getAllowedDeclarativeComponentMapping() ||
      (typeof obj.type === "function" &&
        // except for FBTResults objects.
        !(Array.isArray(obj._contents) && "_stringValue" in obj)))
  );
}

const DeclarativeItemRenderer = (props: DeclarativeItemRendererProps) => {
  const { item } = props;
  const classes = useCenteredStyle();

  let { type: Component } = item;
  const itemProps = { ...item.props };
  const componentMapping = getAllowedDeclarativeComponentMapping();

  if (typeof Component === "string") {
    Component = componentMapping[Component];
  }

  if (itemProps) {
    // Iterate over the props and find nested declarative items.
    for (const prop in itemProps) {
      const propValue = itemProps[prop];

      if (Array.isArray(propValue)) {
        itemProps[prop] = propValue.map((value: any) => {
          if (isDeclarativeItem(value)) {
            return <DeclarativeItemRenderer item={value} />;
          } else {
            return value;
          }
        });
      } else if (isDeclarativeItem(propValue)) {
        itemProps[prop] = <DeclarativeItemRenderer item={propValue} />;
      }
    }
  }

  const result = <Component {...itemProps} />;
  if (item.display === "centered" || item.wrapperStyle) {
    return (
      <div
        className={item.display ? classes.centeredContent : ""}
        style={item.wrapperStyle}
      >
        {result}
      </div>
    );
  }

  return result;
};

export default DeclarativeItemRenderer;
